import React, { useState, useCallback, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
} from "@chakra-ui/react";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { FiX, FiAlertTriangle } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { USER_ROLES } from "newui/features/Auth/constants/UserRoleConstants";
import { useInvitePreflight } from "./hooks/useInvitePreflight";
import { useInviteUserMutation } from "./hooks/useInviteUserMutation";
import { InviteUserConfirmModal } from "./InviteUserConfirmModal";
import type { InviteUserPayload } from "../../types/userInvite";

interface InviteUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InviteFormValues {
  first_name: string;
  last_name: string;
  email: string;
  email_confirmation: string;
  role_id: string;
}

const validationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  email_confirmation: Yup.string()
    .email("Invalid email address")
    .required("Confirm email is required")
    .oneOf([Yup.ref("email")], "Email addresses do not match"),
  role_id: Yup.string().required("Role is required"),
});

const INITIAL_VALUES: InviteFormValues = {
  first_name: "",
  last_name: "",
  email: "",
  email_confirmation: "",
  role_id: "",
};

const ROLE_OPTIONS = USER_ROLES.filter((r) => r.name !== "guest").map((r) => ({
  label: r.label,
  value: String(r.id),
}));

export const InviteUserDrawer: React.FC<InviteUserDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const [confirmPayload, setConfirmPayload] = useState<InviteUserPayload | null>(null);
  const [requiresOverride, setRequiresOverride] = useState(false);

  const preflight = useInvitePreflight();
  const invite = useInviteUserMutation();

  const handleClose = useCallback(() => {
    preflight.clearResult();
    setConfirmPayload(null);
    setRequiresOverride(false);
    invite.reset();
    onClose();
  }, [preflight, invite, onClose]);

  const handleSubmit = (values: InviteFormValues) => {
    const payload: InviteUserPayload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      email_confirmation: values.email_confirmation,
      role_id: Number(values.role_id),
    };
    setRequiresOverride(preflight.result?.requires_override_reason ?? false);
    setConfirmPayload(payload);
  };

  const handleConfirm = (overrideReason?: string) => {
    if (!confirmPayload) {
      return;
    }
    const payload: InviteUserPayload = overrideReason
      ? { ...confirmPayload, domain_override_reason: overrideReason }
      : confirmPayload;

    invite.mutate(payload, {
      onSuccess: () => {
        setConfirmPayload(null);
        handleClose();
      },
    });
  };

  return (
    <>
      <Drawer placement="right" isOpen={isOpen} onClose={handleClose} size="md">
        <DrawerOverlay onClick={handleClose} />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px" className="flex items-center justify-between">
            <span className="text-base font-semibold text-gray-900">Invite User</span>
            <button
              type="button"
              onClick={handleClose}
              className="rounded p-1 text-gray-500 hover:text-gray-700"
              aria-label="Close drawer"
            >
              <FiX size={18} />
            </button>
          </DrawerHeader>

          <DrawerBody className="py-6">
            <Formik
              initialValues={INITIAL_VALUES}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form data-testid="invite-user-form" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormikInputWithLabel
                      name="first_name"
                      label="First name"
                      placeholder="First name"
                      required
                    />
                    <FormikInputWithLabel
                      name="last_name"
                      label="Last name"
                      placeholder="Last name"
                      required
                    />
                  </div>

                  <Field name="email">
                    {({ field, meta }: FieldProps) => (
                      <FormikInputWithLabel
                        name="email"
                        label="Email address"
                        placeholder="name@company.com"
                        type="email"
                        required
                        testId="invite-email-input"
                        onBlur={(e) => {
                          field.onBlur(e);
                          const email = e.target.value.trim();
                          if (email && !meta.error) {
                            preflight.checkEmail(email);
                          }
                        }}
                        onChange={(e) => {
                          setFieldValue("email", e.target.value);
                          preflight.clearResult();
                        }}
                      />
                    )}
                  </Field>

                  <FormikInputWithLabel
                    name="email_confirmation"
                    label="Confirm email address"
                    placeholder="name@company.com"
                    type="email"
                    required
                  />

                  <FormikInputWithLabel
                    name="role_id"
                    type="select"
                    label="Role"
                    placeholder="Select a role…"
                    options={ROLE_OPTIONS}
                    required
                  />

                  {preflight.result?.requires_override_reason && (
                    <div
                      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                      data-testid="domain-override-warning"
                    >
                      <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        This email domain is not on your company&apos;s authorised list. You
                        will need to provide a reason for this exception.
                      </span>
                    </div>
                  )}

                  {preflight.result?.existing_user && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                      A user with this email already exists with status:{" "}
                      <strong>{preflight.result.existing_user.status}</strong>.
                    </div>
                  )}

                  <DrawerFooter
                    borderTopWidth="1px"
                    className="flex justify-end gap-3 px-0 pt-4"
                  >
                    <Button btntype="base" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="invite-submit-button">
                      Review & Send
                    </Button>
                  </DrawerFooter>
                </Form>
              )}
            </Formik>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {confirmPayload && (
        <InviteUserConfirmModal
          isOpen
          email={confirmPayload.email}
          requiresOverrideReason={requiresOverride}
          isLoading={invite.isPending}
          onConfirm={handleConfirm}
          onClose={() => setConfirmPayload(null)}
        />
      )}
    </>
  );
};
